export type PreviewErrorCategory = '学员信息' | '项目匹配' | '校区/授权' | '数据格式/冲突';

/*
 * 学员信息行（组标题行）的 6 类报错。文案与判定顺序都由业务给定，编号对应需求里的提示 1–6：
 *   ① name-mobile-mismatch 学员姓名与手机号不匹配；该手机号对应 X，请核对姓名或手机号
 *   ② mobile-format        手机号格式错误，请填写 11 位手机号
 *   ③ name-empty           学员姓名不能为空
 *   ④ campus-not-found     校区不存在，请选择系统内校区
 *   ⑤ campus-mismatch      学员不属于该校区；学员所属校区：X
 *   ⑥ student-not-found    系统中未找到该学员，请核对姓名和手机号或先完成建档
 * 判定顺序按「有没有填 → 格式对不对 → 手机号查不查得到档案 → 姓名对不对 → 校区存不存在 → 校区对不对」，
 * 声明顺序即判定顺序。每一类在 errorSeeds 里都有样本数据，开发翻预览第一页就能逐条对照。
 */
export type GroupIssueCode =
  | 'name-empty'
  | 'mobile-format'
  | 'student-not-found'
  | 'name-mobile-mismatch'
  | 'campus-not-found'
  | 'campus-mismatch';

export interface GroupIssue {
  readonly code: GroupIssueCode;
  /** 报错归属哪个输入框：红框标在它身上，「修改」也聚焦到它。 */
  readonly field: 'name' | 'mobile' | 'campus';
  readonly message: string;
}

/*
 * 明细行（课程 / 物品）的 9 类报错，文案由业务给定：
 *   ① project-not-authorized 项目在 X 校区未授权；可用校区：A、B
 *   ② project-not-found      项目名称与系统不匹配，请从系统项目中重新选择
 *   ③ category-mismatch      项目类型与系统不匹配；该项目为课程
 *   ④ quantity-positive      购买数量须大于 0
 *   ⑤ quantity-integer       物品数量须为整数
 *   ⑥ amount-exceed          应交金额不能超过标准总额 100.00 元
 *   ⑦ amount-negative        应交金额须大于等于 0
 *   ⑧ charge-type-invalid    收费类型请选择已有选项
 *   ⑨ date-invalid           不是有效日期，请重新选择
 * 每条都归属一个可编辑字段，最后一列里每条都能点、点了聚焦到该字段。
 * 与组级不同：明细行一次可报多条（数量错 + 金额错可以同时存在），判定顺序即声明顺序。
 */
export type ItemIssueCode =
  | 'project-not-authorized'
  | 'project-not-found'
  | 'category-mismatch'
  | 'quantity-positive'
  | 'quantity-integer'
  | 'amount-exceed'
  | 'amount-negative'
  | 'charge-type-invalid'
  | 'date-invalid';

export type ItemIssueField = 'project' | 'category' | 'quantity' | 'dueAmount' | 'chargeType' | 'expiresAt';

export interface ItemIssue {
  readonly code: ItemIssueCode;
  /** 红框标在哪个字段上、最后一列点了聚焦到哪里。 */
  readonly field: ItemIssueField;
  readonly message: string;
}

const groupIssueCategory: Record<GroupIssueCode, PreviewErrorCategory> = {
  'name-empty': '学员信息',
  'mobile-format': '学员信息',
  'student-not-found': '学员信息',
  'name-mobile-mismatch': '学员信息',
  'campus-not-found': '校区/授权',
  'campus-mismatch': '校区/授权',
};

const itemIssueCategory: Record<ItemIssueCode, PreviewErrorCategory> = {
  'project-not-authorized': '校区/授权',
  'project-not-found': '项目匹配',
  'category-mismatch': '项目匹配',
  'quantity-positive': '数据格式/冲突',
  'quantity-integer': '数据格式/冲突',
  'amount-exceed': '数据格式/冲突',
  'amount-negative': '数据格式/冲突',
  'charge-type-invalid': '数据格式/冲突',
  'date-invalid': '数据格式/冲突',
};

function money(value: number) {
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
}

/** 系统里已有的校区：不在这个名单里就是「校区不存在」。下拉选项与判定共用同一份，避免两处走样。 */
export const campusOptions = ['滨江校区', '城北校区', '高新校区'];
const systemCampuses = campusOptions;

/*
 * 系统里存在的项目：类型与授权校区都以它为准，明细行的项目类校验拿当前值跟它比。
 * 「春季英语培优班」故意只在滨江 / 星星美术总校授权——它是「项目未授权」这一条的演示素材，
 * 所以**默认演示数据里不能出现它**（item 默认排的是全校区授权的课），否则城北校区的几百笔
 * 订单会集体报错，把演示数据淹掉。
 */
const systemProjects: Record<string, { readonly category: '课程' | '物品'; readonly campuses: readonly string[] }> = {
  '寒假数学提升班': { category: '课程', campuses: ['滨江校区', '城北校区', '高新校区'] },
  '秋季语文阅读班': { category: '课程', campuses: ['滨江校区', '城北校区', '高新校区'] },
  '春季英语培优班': { category: '课程', campuses: ['滨江校区', '星星美术总校'] },
  '实验材料包': { category: '物品', campuses: ['滨江校区', '城北校区'] },
};

/**
 * 系统里登记过的项目名：明细行的「课程 / 物品名称」下拉选项与「项目名称与系统不匹配」判定共用这一份，
 * 下拉只给已登记的名字，用户选完就是合法值——这条报错也因此才改得动。
 */
export const projectOptions = Object.keys(systemProjects);

/** 收费类型是枚举，不是自由文本：不在名单里就报「请选择已有选项」。 */
export const chargeTypeOptions = ['续费', '新增', '补缴', '转班'];

export interface PreviewItem {
  readonly category: '课程' | '物品';
  readonly chargeType: string;
  readonly dueAmount: number;
  readonly expiresAt: string;
  readonly id: string;
  /** 当前这条明细的全部报错（可多条）；由 itemIssuesOf() 实时判定，改对了自动消失。 */
  readonly issues: readonly ItemIssue[];
  readonly originalRow: number;
  readonly project: string;
  readonly quantity: number;
  readonly unitPrice: number;
}

export interface PreviewOrder {
  readonly campus: string;
  readonly performanceCampus: string;
  readonly expectedCampus: string;
  readonly expectedMobile: string;
  readonly expectedName: string;
  readonly commission: string;
  readonly errors: readonly PreviewErrorCategory[];
  /** 学员信息行当前那条报错的代码；由 groupIssueOf() 实时判定，改对了就消失。 */
  readonly groupIssue?: GroupIssueCode;
  readonly id: string;
  readonly items: readonly PreviewItem[];
  readonly mobile: string;
  readonly name: string;
  readonly note: string;
  readonly owner: string;
  readonly role: string;
}

/*
 * 演示数据的错误分布：index 0 无错，index 1–16 各带至少一条错，翻预览第一页（默认 50 条/页）就看得到全部形态。
 * 组级 6 类 + 明细 9 类每类至少一份，值都按该类专门造过——比如「校区不存在」那组写「待匹配校区」、
 * 「项目未授权」那组把课程换成只在滨江/星星美术总校授权的「春季英语培优班」、校区又是城北。
 *
 * 有几单故意带多条（2–3 条）：真实导入里一笔订单常常错好几个字段，全都是「1 项待修正」就看不出
 * 数量分布，也测不出同一行多条报错的排版。
 */
interface ErrorSeed {
  /** 学员信息行要演示的报错。 */
  readonly group?: GroupIssueCode;
  /** 明细行要演示的报错：第几条明细 → 那几条代码。组级与明细可同时给，模拟一单错好几个字段。 */
  readonly items?: readonly { readonly index: 0 | 1; readonly codes: readonly ItemIssueCode[] }[];
}

const errorSeeds: readonly ErrorSeed[] = [
  { group: 'name-mobile-mismatch' },
  { items: [{ index: 0, codes: ['amount-exceed'] }] },
  { group: 'mobile-format' },
  { group: 'name-empty' },
  // 组级错 + 明细错同时存在：这一单共 2 项待修正。
  { group: 'student-not-found', items: [{ index: 0, codes: ['charge-type-invalid'] }] },
  { items: [{ index: 0, codes: ['category-mismatch'] }, { index: 1, codes: ['project-not-found'] }] },
  // 项目未授权：两门课都在城北未授权，所以这一单会出两条同类提示。
  { items: [{ index: 0, codes: ['project-not-authorized'] }] },
  { items: [{ index: 0, codes: ['project-not-found'] }] },
  { items: [{ index: 0, codes: ['quantity-positive'] }, { index: 1, codes: ['date-invalid'] }] },
  { items: [{ index: 0, codes: ['quantity-integer'] }, { index: 1, codes: ['amount-exceed'] }] },
  { items: [{ index: 0, codes: ['amount-negative'] }] },
  { items: [{ index: 0, codes: ['charge-type-invalid'] }] },
  // 同一条明细错两个字段 + 另一条明细再错一个：这一单共 3 项待修正。
  { items: [{ index: 0, codes: ['date-invalid', 'charge-type-invalid'] }, { index: 1, codes: ['amount-negative'] }] },
  { group: 'campus-not-found' },
  { group: 'campus-mismatch' },
  { group: 'campus-not-found' },
];

/*
 * 按要演示的报错代码把值造错：造出来的值必须真的能触发该类判定（最后会由 itemIssuesOf 复核），
 * 否则页面显示的报错和种子写的对不上。组合错要避开互相矛盾——比如数量填 0 又让金额「超过标准总额」，
 * 那标准总额就是 0，文案会变成「不能超过标准总额 0.00 元」。
 */
function createItem(orderIndex: number, itemIndex: number, originalRow: number, codes: readonly ItemIssueCode[] = []): PreviewItem {
  const baseProject = itemIndex === 0 ? '寒假数学提升班' : '秋季语文阅读班';
  let project = baseProject;
  let category: '课程' | '物品' = '课程';
  let unitPrice = itemIndex === 0 ? 100 : 80;
  let quantity = itemIndex === 0 ? 10 : 20;
  let dueAmount = itemIndex === 0 ? 900 : 1440;
  let chargeType = '续费';
  let expiresAt = itemIndex === 0 ? '2027-02-28' : '2027-06-30';

  // 只在滨江 / 星星美术总校授权：配城北校区用，演示「项目在城北校区未授权；可用校区：…」。
  if (codes.includes('project-not-authorized')) project = '春季英语培优班';
  if (codes.includes('project-not-found')) project = `${baseProject}（旧名称）`;
  // 系统里这两个项目都是课程，填成物品即触发「该项目为课程」。
  if (codes.includes('category-mismatch')) category = '物品';
  // 物品数量 2.5 件：换成系统里本来就是物品的「实验材料包」，才不会顺带报「项目类型与系统不匹配」。
  if (codes.includes('quantity-integer')) { project = '实验材料包'; category = '物品'; unitPrice = 180; quantity = 2.5; dueAmount = 200; }
  if (codes.includes('quantity-positive')) quantity = 0;
  /*
   * 标准总额 50 × 2 = 100，应交 110 → 文案正好是业务给的那句「不能超过标准总额 100.00 元」。
   * 第二条明细用另一组数（80 × 20 = 1600），免得两条演示的金额一模一样、看不出金额是按明细算的。
   */
  if (codes.includes('amount-exceed')) {
    unitPrice = itemIndex === 0 ? 50 : 80;
    quantity = itemIndex === 0 ? 2 : 20;
    dueAmount = unitPrice * quantity + 10;
  }
  if (codes.includes('amount-negative')) dueAmount = -50;
  if (codes.includes('charge-type-invalid')) chargeType = '待确认';
  if (codes.includes('date-invalid')) expiresAt = '2027-13-45';
  // 数量改成 0 后标准总额也是 0：金额必须跟着归零，否则会多出一条矛盾的金额提示。
  if (quantity === 0 && !codes.includes('amount-negative')) dueAmount = 0;

  return {
    category,
    chargeType,
    dueAmount,
    expiresAt,
    id: `${orderIndex}-${itemIndex}`,
    issues: [],
    originalRow,
    project,
    quantity,
    unitPrice,
  };
}

export function buildPreviewOrders(count = 1200): PreviewOrder[] {
  const rowByOrder = (index: number) => index * 2 + 2;
  return Array.from({ length: count }, (_, index) => {
    const seed = index === 0 ? undefined : errorSeeds[index - 1];
    const pad = String(index + 1).padStart(4, '0');
    // 档案里的正确值：expected* 就是「系统里该学员/该校区是什么样」，用户填的值跟它比。
    let expectedName = index === 0 ? '林小满' : index === 1 ? '周一诺' : `演示学员${pad}`;
    const expectedMobile = `1380000${pad}`;
    const expectedCampus = index % 3 === 1 ? '城北校区' : '滨江校区';

    // 组级演示数据：每类把「用户填的值」按该类造错，档案值保持正确，判定与文案才对得上。
    let name = expectedName;
    let mobile = expectedMobile;
    let campus = expectedCampus;
    const groupCode = seed?.group;
    if (groupCode) {
      if (groupCode === 'name-empty') name = '';
      if (groupCode === 'mobile-format') mobile = '1380000';
      // 未建档：手机号换成档案里没有的号（11 位、格式合法），所以判定只会命中「找不到该学员」。
      if (groupCode === 'student-not-found') mobile = `1390000${pad}`;
      // 姓名与手机号不匹配：手机号是档案里「林小满」的，姓名却填成了别人，提示里要能报出档案姓名。
      if (groupCode === 'name-mobile-mismatch') { name = '张小满'; expectedName = '林小满'; }
      if (groupCode === 'campus-not-found') campus = '待匹配校区';
      // 学员不属于该校区：填系统内的另一个校区（档案里所属校区仍是 expectedCampus）。
      if (groupCode === 'campus-mismatch') campus = '高新校区';
    }

    const codesOf = (itemIndex: 0 | 1) => seed?.items?.find((entry) => entry.index === itemIndex)?.codes ?? [];
    const items = [
      createItem(index, 0, rowByOrder(index), codesOf(0)),
      createItem(index, 1, rowByOrder(index) + 1, codesOf(1)),
    ];
    const order: PreviewOrder = {
      campus,
      // 业绩信息不来自上传表格，导入后由用户补填。
      performanceCampus: '',
      expectedCampus,
      expectedMobile,
      expectedName,
      commission: '',
      errors: [],
      id: `order-${index + 1}`,
      items,
      mobile,
      name,
      note: '',
      owner: '',
      role: '',
    };
    /*
     * 组级与明细的报错都不直接抄种子，而是拿造好的数据再判一遍：种子一旦跟判定对不上，
     * 这里落的就是实际那条错，测试（各类各出现一次）会立刻挂，
     * 不会出现「数据写着 A 类、页面显示 B 类」的错位演示。
     */
    return withIssues(order);
  });
}

/*
 * 学员信息行的校验：一次只报一条，按「有没有填 → 格式 → 手机号查不查得到档案 → 姓名 → 校区存不存在 → 校区对不对」
 * 取第一个命中的。判定只看当前值和 expected*，所以用户改对了提示自动消失——跟 withGroupIssue 同源。
 */
export function groupIssueOf(order: PreviewOrder): GroupIssue | undefined {
  const name = order.name.trim();
  const mobile = order.mobile.trim();
  const campus = order.campus.trim();

  if (!name) return { code: 'name-empty', field: 'name', message: '学员姓名不能为空' };
  if (!/^1\d{10}$/.test(mobile)) return { code: 'mobile-format', field: 'mobile', message: '手机号格式错误，请填写 11 位手机号' };
  if (mobile !== order.expectedMobile) return { code: 'student-not-found', field: 'mobile', message: '系统中未找到该学员，请核对姓名和手机号或先完成建档' };
  if (name !== order.expectedName) return { code: 'name-mobile-mismatch', field: 'name', message: `学员姓名与手机号不匹配；该手机号对应 ${order.expectedName}，请核对姓名或手机号` };
  if (!systemCampuses.includes(campus)) return { code: 'campus-not-found', field: 'campus', message: `校区不存在，请选择系统内校区；推荐校区：${order.expectedCampus}` };
  if (campus !== order.expectedCampus) return { code: 'campus-mismatch', field: 'campus', message: `学员不属于该校区；学员所属校区：${order.expectedCampus}` };
  return undefined;
}

/** UI 侧统一按列表消费（一个组最多一条组级错，保留数组是为了不改渲染结构）。 */
export function groupIssueList(order: PreviewOrder): readonly GroupIssue[] {
  const issue = groupIssueOf(order);
  return issue ? [issue] : [];
}

/*
 * 明细行的校验：与组级不同，这里一次可报多条（数量错和金额错能同时存在），
 * 顺序按「项目 → 项目类型 → 数量 → 金额 → 收费类型 → 有效期」。判定只看当前值和系统名单，
 * 所以用户改对了提示自动消失、改错了换成新的那条——跟 withIssues 同源。
 */
export function itemIssuesOf(item: PreviewItem, campus: string): readonly ItemIssue[] {
  const issues: ItemIssue[] = [];
  const project = item.project.trim();
  const known = systemProjects[project];
  const standard = item.unitPrice * item.quantity;

  if (!known) {
    issues.push({ code: 'project-not-found', field: 'project', message: '项目名称与系统不匹配，请从系统项目中重新选择' });
  } else {
    // 校区本身就不在系统里时组级已经报了「校区不存在」，这里不再叠一条未授权，免得同一个问题报两遍。
    if (systemCampuses.includes(campus) && !known.campuses.includes(campus)) {
      issues.push({ code: 'project-not-authorized', field: 'project', message: `项目在${campus}未授权；可用校区：${known.campuses.join('、')}` });
    }
    if (item.category !== known.category) {
      issues.push({ code: 'category-mismatch', field: 'category', message: `项目类型与系统不匹配；该项目为${known.category}` });
    }
  }
  if (item.quantity <= 0) issues.push({ code: 'quantity-positive', field: 'quantity', message: '购买数量须大于 0' });
  else if (!Number.isInteger(item.quantity)) issues.push({ code: 'quantity-integer', field: 'quantity', message: '物品数量须为整数' });
  if (item.dueAmount < 0) issues.push({ code: 'amount-negative', field: 'dueAmount', message: '应交金额须大于等于 0' });
  else if (item.dueAmount > standard) issues.push({ code: 'amount-exceed', field: 'dueAmount', message: `应交金额不能超过标准总额 ${money(standard)} 元` });
  if (!chargeTypeOptions.includes(item.chargeType.trim())) issues.push({ code: 'charge-type-invalid', field: 'chargeType', message: '收费类型请选择已有选项' });
  // 空值不报：物品类明细本来就没有有效期。
  if (item.expiresAt.trim() && !isDate(item.expiresAt.trim())) issues.push({ code: 'date-invalid', field: 'expiresAt', message: '不是有效日期，请重新选择' });
  return issues;
}

/** 日期必须是真实存在的 yyyy-mm-dd：'2027-13-45' 形状对但不是有效日期。 */
function isDate(text: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day);
}

/*
 * 一笔订单涉及哪几类错误：组级 + 全部明细，一条报错一个元素（**不去重**）。
 * 不去重是给分类计数用的——同一单错了两个金额字段，「数据格式/冲突」就该记 2；
 * 筛选只看 includes，重复不影响。
 */
export function orderErrorsOf(order: PreviewOrder): readonly PreviewErrorCategory[] {
  const list: PreviewErrorCategory[] = [];
  const group = groupIssueOf(order);
  if (group) list.push(groupIssueCategory[group.code]);
  for (const item of order.items) for (const issue of item.issues) list.push(itemIssueCategory[issue.code]);
  return list;
}

/** 一笔订单一共有几条报错：组行末列「N 项待修正」就显示这个数。 */
export function orderIssueCount(order: PreviewOrder): number {
  return groupIssueList(order).length + order.items.reduce((count, item) => count + item.issues.length, 0);
}

/** 改值后重算全部报错：组级与明细都按当前值重判，改对了消失、改错了换成新的那条。 */
export function withIssues(order: PreviewOrder): PreviewOrder {
  const items = order.items.map((item) => ({ ...item, issues: itemIssuesOf(item, order.campus) }));
  const next = { ...order, items };
  const issue = groupIssueOf(next);
  return { ...next, errors: orderErrorsOf(next), groupIssue: issue?.code };
}

export function getItemDiscount(item: PreviewItem): number {
  return Math.max(0, item.unitPrice * item.quantity - item.dueAmount);
}

export function getOrderTotal(order: PreviewOrder): number {
  return order.items.reduce((total, item) => total + item.dueAmount, 0);
}

export function getOrderDiscount(order: PreviewOrder): number {
  return order.items.reduce((total, item) => total + getItemDiscount(item), 0);
}
